/**
 * Data loader — fetches location data from Google Sheets (CSV) or JSON endpoints.
 * Uses PapaParse (loaded from CDN) for robust CSV parsing.
 * Normalizes all data into a consistent location object format.
 */

import { DEFAULTS } from './config.js';

const PAPAPARSE_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.4.1/papaparse.min.js';

/**
 * Dynamically load PapaParse if not already present.
 */
function loadPapaParse() {
  if (window.Papa) return Promise.resolve();
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${PAPAPARSE_CDN}"]`)) {
      const check = setInterval(() => {
        if (window.Papa) { clearInterval(check); resolve(); }
      }, 50);
      return;
    }
    const script = document.createElement('script');
    script.src = PAPAPARSE_CDN;
    script.onload = resolve;
    script.onerror = () => reject(new Error('Failed to load PapaParse'));
    document.head.appendChild(script);
  });
}

/**
 * Fetch and parse location data from a published Google Sheet.
 * Uses the gviz CSV export endpoint — no API key required.
 *
 * @param {string} sheetId — Google Sheets document ID
 * @returns {Array<Object>} Normalized location objects
 */
export async function fetchSheetData(sheetId) {
  await loadPapaParse();

  const url = DEFAULTS.sheetCsvUrl(sheetId);
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch sheet data: ${response.status}`);
  }

  const csvText = await response.text();
  const result = window.Papa.parse(csvText, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h) => h.trim().toLowerCase().replace(/\s+/g, '_'),
  });

  if (result.errors.length > 0) {
    console.warn('[PilotMaps] CSV parse warnings:', result.errors);
  }

  console.log('[PilotMaps] Parsed headers:', result.meta.fields);
  if (result.data.length > 0) {
    console.log('[PilotMaps] First row:', JSON.stringify(result.data[0]));
  }

  const locations = result.data
    .filter(row => {
      const hasLat = row.lat || row.latitude;
      const hasLng = row.lng || row.lon || row.longitude;
      return hasLat && hasLng;
    })
    .map(normalizeLocation);

  console.log(`[PilotMaps] Loaded ${locations.length} locations`);
  return locations;
}

/**
 * Fetch location data from a JSON endpoint.
 *
 * @param {string} url — URL returning a JSON array of location objects
 * @returns {Array<Object>} Normalized location objects
 */
export async function fetchJsonData(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch JSON data: ${response.status}`);
  }

  const data = await response.json();
  const locations = Array.isArray(data) ? data : data.locations || data.data || [];
  return locations.map(normalizeLocation);
}

/**
 * Normalize a raw location object into the standard format.
 * Handles common column name variations.
 */
function normalizeLocation(raw) {
  // Build full address from separate fields if city/state exist
  let address = raw.address || raw.street_address || '';
  if (raw.city) {
    const parts = [address, raw.city];
    if (raw.state) parts.push(raw.state);
    if (raw.zip) parts.push(raw.zip);
    address = parts.filter(Boolean).join(', ');
  }

  return {
    // Required fields
    name: raw.name || raw.location_name || raw.title || 'Unnamed Location',
    lat: parseFloat(raw.latitude || raw.lat),
    lng: parseFloat(raw.longitude || raw.lng || raw.lon),
    address: address,

    // Optional standard fields
    phone: raw.phone || raw.telephone || raw.phone_number || '',
    hours: raw.hours || raw.hours_of_operation || raw.operating_hours || '',
    url: raw.url || raw.website || raw.link || '',
    specialty: raw.specialty || raw.specialties || '',
    fax: raw.fax || '',
    parking: raw.parking || raw.parking_info || '',
    photo: raw.photo || raw.image || raw.image_url || raw.photo_url || '',

    // Status fields
    open_time: raw.open_time || raw.opening_time || '',
    close_time: raw.close_time || raw.closing_time || '',
    accepting_new_patients: raw.accepting_new_patients || raw.accepting_patients || '',
    services: raw.services || raw.service || '',

    // Preserve all original fields for filtering
    _raw: raw,
  };
}
